import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { ColorRing } from "react-loader-spinner";
import API_BASE_URL from "../../../../Constants";
import { useNavigate } from 'react-router-dom';

// Fix for default icon issue with Leaflet
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const InspectionsMapView = () => {
    const [inspections, setInspections] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [geocodedInspections, setGeocodedInspections] = useState([]);
    const [isFetched, setIsFetched] = useState(false);
    const navigate = useNavigate();

    // Function to geocode an address using the OpenStreetMap Nominatim API
    const getCoordinatesFromAddress = async (address) => {
        try {
            const fullAddress = `${address}, New York, NY`;
            const cacheKey = `geocode_${fullAddress}`;

            // Check localStorage cache first
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                console.log("Loaded geocode from cache:", fullAddress);
                return JSON.parse(cached);
            }

            console.log("Geocoding address:", fullAddress);
            const response = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(fullAddress)}`);

            if (!response.ok) {
                console.error("Photon API error:", response.status);
                return null;
            }

            const data = await response.json();
            console.log("Geocoding response:", data);

            if (data && data.features && data.features.length > 0) {
                const [longitude, latitude] = data.features[0].geometry.coordinates;
                const coords = { latitude, longitude };

                // Store in cache
                localStorage.setItem(cacheKey, JSON.stringify(coords));
                console.log("Successfully geocoded and cached:", coords);

                return coords;
            }
        } catch (error) {
            console.error("Error geocoding address:", error);
        }
        return null;
    };

    // Fetch inspections data on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);

                const inspResponse = await fetch(
                    `${API_BASE_URL}/api/inspections`,
                    { credentials: "include" }
                );

                if (!inspResponse.ok) {
                    throw new Error(`HTTP error! status: ${inspResponse.status}`);
                }

                const inspData = await inspResponse.json();
                // const inspectionsData = inspData.content || [];
                setInspections(inspData);
                console.log("Inspections", inspData);

                setIsFetched(true);
            } catch (err) {
                console.error("Error fetching data:", err);
                // setInspections([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    // useEffect to geocode inspections with buildingSiteStored - improved version
    useEffect(() => {
        if (!isFetched || !inspections.length) return;

        console.log("Starting geocoding process for", inspections.length, "inspections");
        setIsLoading(true);

        const geocodeInspections = async () => {
            const results = [];

            for (const inspection of inspections) {
                if (inspection.buildingSiteStored) {
                    try {

                        console.log("Processing inspection:", inspection.inspectionId, inspection.buildingSiteStored);
                        const coords = await getCoordinatesFromAddress(inspection.buildingSiteStored);

                        if (coords) {
                            console.log("Successfully geocoded inspection:", inspection.inspectionId);
                            results.push({
                                ...inspection,
                                latitude: coords.latitude,
                                longitude: coords.longitude
                            });
                        } else {
                            console.log("Failed to geocode inspection:", inspection.inspectionId);
                        }
                    } catch (error) {
                        console.error("Error processing inspection:", inspection.inspectionId, error);
                    }
                } else {
                    console.log("No buildingSiteStored for inspection:", inspection.inspectionId);
                }
            }

            console.log("Geocoding complete. Results:", results.length);
            setGeocodedInspections(results);
            setIsLoading(false);
        };

        geocodeInspections();
    }, [isFetched, inspections]);

    // Create custom icon for markers
    const createCustomIcon = (status) => {
        // Define colors based on inspection status
        const getStatusColor = () => {
            switch (status?.toLowerCase()) {
                case 'completed':
                    return '#4CAF50'; // Green
                case 'scheduled':
                    return '#2196F3'; // Blue
                case 'overdue':
                    return '#F44336'; // Red
                case 'active':
                    return '#9C27B0'; // Purple
                default:
                    return '#FFA000'; // Orange for unknown/other status
            }
        };

        const svgIcon = `
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41">
                <path fill="${getStatusColor()}" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 6.9 11.8 26.3 12.2 27L12.5 41l.3-1.5C13.2 38.8 25 19.4 25 12.5 25 5.6 19.4 0 12.5 0z"/>
                <circle cx="12.5" cy="12.5" r="5.5" fill="#FFF"/>
            </svg>
        `;

        return new L.DivIcon({
            html: svgIcon,
            className: 'custom-div-icon',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
        });
    };

    // Get popup content for each marker


    const getPopupContent = (inspection) => {
        // Helper function to determine status color
        const getStatusColor = (status) => {
            const statusMap = {
                active: "text-purple-600",
                completed: "text-green-600",
                scheduled: "text-blue-600",
                overdue: "text-red-600",
                default: "text-orange-600"
            };
            return statusMap[status?.toLowerCase()] || statusMap.default;
        };

        return (
            <div className="w-72 overflow-hidden">
                {/* Header with status */}
                <div className="pb-2 mb-2 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-800">
                            Inspection #{inspection.inspectionId}
                        </h3>
                        <span className={`font-medium ${getStatusColor(inspection.status)}`}>
                            {inspection.status}
                        </span>
                    </div>
                </div>

                {/* Main content */}
                <div className="mb-2">
                    {/* Main details grid */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <div>
                            <div className="text-sm text-gray-500">Inspection #:</div>
                            <div className="font-medium">{inspection.inspectionNumber || "—"}</div>
                        </div>

                        <div>
                            <div className="text-sm text-gray-500">Building ID:</div>
                            <div className="font-medium">{inspection.buildingId || "—"}</div>
                        </div>

                        <div className="col-span-2">
                            <div className="text-sm text-gray-500">Address:</div>
                            <div className="font-medium">{inspection.buildingSiteStored || "Not specified"}</div>
                        </div>

                        <div>
                            <div className="text-sm text-gray-500">Agency ID:</div>
                            <div className="font-medium">{inspection.agencyId || "—"}</div>
                        </div>

                        <div>
                            <div className="text-sm text-gray-500">Test:</div>
                            <div className="font-medium">{inspection.test || "—"}</div>
                        </div>

                        <div>
                            <div className="text-sm text-gray-500">Last Date:</div>
                            <div className="font-medium">{inspection.lastDate || "N/A"}</div>
                        </div>

                        <div>
                            <div className="text-sm text-gray-500">Next Date:</div>
                            <div className="font-medium">{inspection.nextDate || "N/A"}</div>
                        </div>
                    </div>
                </div>

                {/* Footer with button */}
                <div className="pt-2 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={() => navigate('/inspections-violations/inspections-list')}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                        Back to List
                    </button>
                </div>
            </div>
        );
    };

    // Calculate map bounds based on all valid coordinates
    const getBounds = () => {
        const validCoordinates = geocodedInspections
            .filter(insp => insp.latitude && insp.longitude)
            .map(insp => [insp.latitude, insp.longitude]);

        console.log("Valid coordinates for map bounds:", validCoordinates.length);

        if (validCoordinates.length === 0) {
            console.log("No valid coordinates, using default NYC bounds");
            return [[40.7831, -73.9712], [40.7831, -73.9712]]; // Default to NYC
        }

        const lats = validCoordinates.map(coord => coord[0]);
        const lngs = validCoordinates.map(coord => coord[1]);

        const bounds = [
            [Math.min(...lats), Math.min(...lngs)],
            [Math.max(...lats), Math.max(...lngs)]
        ];

        console.log("Calculated map bounds:", bounds);
        return bounds;
    };

    return (
        <div className="h-screen w-full flex flex-col pt-36">
            <div className="flex justify-between items-center p-4 bg-white border-b shadow-sm z-10">
                <h1 className="text-2xl font-bold text-gray-800">Inspections Map View</h1>
                <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center">
                            <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1"></span>
                            <span className="text-sm">Completed</span>
                        </div>
                        <div className="flex items-center">
                            <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1"></span>
                            <span className="text-sm">Scheduled</span>
                        </div>
                        <div className="flex items-center">
                            <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1"></span>
                            <span className="text-sm">Overdue</span>
                        </div>
                        <div className="flex items-center">
                            <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-1"></span>
                            <span className="text-sm">Active</span>
                        </div>
                        <div className="flex items-center">
                            <span className="inline-block w-3 h-3 rounded-full bg-orange-500 mr-1"></span>
                            <span className="text-sm">Other</span>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <button
                            onClick={() => navigate('/inspections-violations/inspections-list')}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Back to List
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex-1 relative pt-2">
                {isLoading ? (
                    <div className="absolute inset-0 flex justify-center items-center bg-white">
                        <ColorRing
                            visible
                            height="80"
                            width="80"
                            ariaLabel="color-ring-loading"
                            wrapperStyle={{}}
                            wrapperClass="color-ring-wrapper"
                            colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
                        />
                    </div>
                ) : (
                    <>
                        {geocodedInspections.length === 0 ? (
                            <div className="absolute inset-0 flex flex-col justify-center items-center bg-white">
                                <p className="text-xl text-gray-700 mb-4">No geocoded locations available</p>
                                <p className="text-gray-500">Please check that the inspections have valid building addresses</p>
                            </div>
                        ) : (
                            <MapContainer
                                bounds={getBounds()}
                                zoom={12}
                                style={{ height: '100%', width: '100%' }}
                                className="z-0"
                            >
                                <LayersControl position="topright">
                                    <LayersControl.BaseLayer checked name="Default">
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='&copy; OpenStreetMap contributors'
                                        />
                                    </LayersControl.BaseLayer>
                                    <LayersControl.BaseLayer name="Satellite">
                                        <TileLayer
                                            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                            attribution='Tiles &copy; Esri &mdash; Source: Esri, USGS, NOAA'
                                        />
                                    </LayersControl.BaseLayer>
                                </LayersControl>

                                {geocodedInspections
                                    .filter(inspection => inspection.latitude && inspection.longitude)
                                    .map((inspection) => (
                                        <Marker
                                            key={inspection.inspectionId}
                                            position={[inspection.latitude, inspection.longitude]}
                                            icon={createCustomIcon(inspection.status)}
                                        >
                                            <Popup>
                                                {getPopupContent(inspection)}
                                            </Popup>
                                        </Marker>
                                    ))
                                }
                            </MapContainer>
                        )}
                    </>
                )}
            </div>

            {/* Debug info */}
            <div className="p-2 bg-gray-100 text-xs">
                <p>Total inspections: {inspections.length}</p>
                <p>Geocoded inspections: {geocodedInspections.length}</p>
            </div>
        </div>
    );
};

export default InspectionsMapView;